import { errorMessageFrom } from "../../../../../../utils/formErrors";
import React,{useState, useEffect} from 'react';
import { Box, Stack, CircularProgress} from '@mui/material';
import TopBar from "../../../../../../component/TopBar";
import ActiveBar from "../../../../../../component/PolicyNavbar";
import BtnPrimary from '../../../../../../component/PrimaryButton';
import BtnSecondary from '../../../../../../component/SecondaryButton';
import TypeBtn from '../../../../../../component/AddRemoveButton';
import FormField from '../../../../../../component/Formfield';
import DateField from "../../../../../../component/DateField";
import {getAllLeaveType,createLeavePolicy} from "../../../../../../services/leave";
import {useLocation, useNavigate} from "react-router-dom";
import ErrorMsg from '../../../../../../component/ErrorMsg';
import { useNotify } from '../../../../../../component/NotificationProvider';
import { WizardCard, FormSection, FieldGrid, FieldLabel, WizardActions } from '../../../../../../component/wizard';
import { tokens } from '../../../../../../theme';

const LeavePolicyDetails = () => {
  const [apiCall,setApiCall]=useState(false);
  const navigate = useNavigate();
  const notify = useNotify();
  const {state} = useLocation();
  const [policyForm,setPolicyForm] = useState({
    code:"",
    name:"",
    description:"",
    startDate:null,
    endDate:null,
  })
  const [startDateDialogOpen, setStartDateDialogOpen] = useState(false);
  const [endDateDialogOpen, setEndDateDialogOpen] = useState(false);
  const [leaveQuantities,setLeaveQuantities] = useState([]);
  const [leaveOptions, setLeaveOptions] = useState([]);
  const [validationErrors, setValidationErrors] = useState({code:"",
                                                            name:"",
                                                            description:"",
                                                          });
  const [error,setError]=useState("");

  useEffect(() => {   
    fetchLeaveTypeOptions();
  }, [state]); 

  const fetchLeaveTypeOptions = async () => {
    try {
      const res = await getAllLeaveType();
      const mappedOptions = (res.data || []).map(option => ({ value: option._id, name: option.name }));
      setLeaveOptions(mappedOptions);
    } catch (error) {
      notify.error(error?.message || "Failed to load leave types");
    }
  };

   const handleAddLeaveQuantityClick = () =>{
      setLeaveQuantities([...leaveQuantities,{leaveType:"",quantity:""}])
    }

    const handleRemoveLeaveQuantityClick = (index) => {
      let updatedLeaveQuantities = [...leaveQuantities];
      updatedLeaveQuantities.splice(index, 1);
      setLeaveQuantities(updatedLeaveQuantities);
    }; 

    const handleLeaveChange = (index, fieldName, value) => {
      const updatedLeaveQuantities = [...leaveQuantities];
      updatedLeaveQuantities[index][fieldName] = value;
      setLeaveQuantities(updatedLeaveQuantities);
    };

    const PolicyFormData = {
      ...policyForm,
      leaveQuantities,
      startDate: (new Date(policyForm.startDate)).getTime(),
      endDate: (new Date(policyForm.endDate)).getTime(),
      status: "draft", // stays draft through the wizard; grants no balances until published
    };

    const handleNextClick = async() => {

      const errors ={};
      if (!PolicyFormData.code) {
        errors.code="Code is required"
      }
      if (!PolicyFormData.name) {
          errors.name="Name is required"
      }
      if (!PolicyFormData.startDate) {
         errors.startDate="Start Date is required"
      }
      if (!PolicyFormData.endDate) {
         errors.endDate="End Date is required"
      }
      if (!PolicyFormData.description) {
        errors.description="Description is required"
     }
      
      if (Object.keys(errors).length > 0) {
        setValidationErrors(errors);
        return;
      }

      setApiCall(true);
      try {
          const res = await createLeavePolicy(PolicyFormData);
          if(res.success){
            notify.success("Leave policy created");
            navigate("/app/leave-policy/assign-policy",{state:{policyId: res.data._id}});
          } else {
              setError(res.message);
              notify.error(errorMessageFrom(res, "Policy creation failed"));
          }
      } catch (error) {
          notify.error(error?.message || "Something went wrong creating the policy");
      } finally {
          setApiCall(false);
      }
  };

  const handleChange = (field, value) => {
    const updatedValue = field === "code" ? value?.toUpperCase() : value;
    setPolicyForm(prevState => ({...prevState, [field]: updatedValue}));
    setValidationErrors(prevState => ({...prevState, [field]: ""}));
    setError("");
  };
  
  return (
    <Box sx={{width:"100%"}}>
        <TopBar MainHeading={"Leave Policy"} breadcrumbs={[{label:"Leave Policy", href:"/app/leave-policy"},{label:"Create"}]}/>
        <WizardCard>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: "1.5rem" }}>
              <ActiveBar active={"Policy Details"}/>
              <BtnSecondary to={"/app/leave-policy"} sx={{ borderRadius: "8px" }}>
                  Back
              </BtnSecondary>
          </Stack>

          <FormSection title="Policy details" description="Name the policy and set the period it applies for.">
            <FieldGrid>
              <Box>
                <FieldLabel required>Policy Code</FieldLabel>
                <FormField type="text" value={policyForm?.code} validation={validationErrors?.code} onChange={(e) => handleChange('code', e.target.value)} />
              </Box>
              <Box>
                <FieldLabel required>Policy Name</FieldLabel>
                <FormField type="text" value={policyForm?.name} validation={validationErrors?.name} onChange={(e) => handleChange('name', e.target.value)} />
              </Box>
              <Box>
                <FieldLabel required>Start Date</FieldLabel>
                <DateField value={policyForm.startDate} isDialogOpen={startDateDialogOpen} validation={validationErrors.startDate}
                  openDialog={() => setStartDateDialogOpen(true)} closeDialog={() => setStartDateDialogOpen(false)}
                  onSelect={(date) => { handleChange('startDate', date); setStartDateDialogOpen(false); }} />
              </Box>
              <Box>
                <FieldLabel required>End Date</FieldLabel>
                <DateField value={policyForm.endDate} isDialogOpen={endDateDialogOpen} validation={validationErrors.endDate}
                  openDialog={() => setEndDateDialogOpen(true)} closeDialog={() => setEndDateDialogOpen(false)}
                  onSelect={(date) => { handleChange('endDate', date); setEndDateDialogOpen(false); }} />
              </Box>
            </FieldGrid>
            <Box sx={{ mt: "1.25rem" }}>
              <FieldLabel required>Description</FieldLabel>
              <FormField type="text" value={policyForm?.description} validation={validationErrors.description} fullwidth row={4}
                onChange={(e) => handleChange('description', e.target.value)} />
            </Box>
          </FormSection>

          <FormSection title="Leave entitlements" description="Add the leave types this policy grants and how many days each.">
            {leaveQuantities?.map((item, index) => (
              <Stack key={index} direction={{ xs: "column", sm: "row" }} alignItems={{ sm: "flex-end" }} spacing={1.5}
                sx={{ mb: "1rem", p: "1rem 1.25rem", borderRadius: "12px", border: `1px solid ${tokens.border}`, background: tokens.surface }}>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel>Leave Type</FieldLabel>
                  <FormField type="select" options={leaveOptions} value={item?.leaveType} onChange={(e) => handleLeaveChange(index, 'leaveType', e.target.value)} fullwidth />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FieldLabel>Quantity (days)</FieldLabel>
                  <FormField type="text" value={item?.quantity} onChange={(e) => handleLeaveChange(index, 'quantity', e.target.value)} fullwidth />
                </Box>
                <TypeBtn type="remove" onClick={() => handleRemoveLeaveQuantityClick(index)}>Remove</TypeBtn>
              </Stack>
            ))}
            <TypeBtn type="add" onClick={handleAddLeaveQuantityClick}>Add Leave Type</TypeBtn>
          </FormSection>

          <ErrorMsg>{error}</ErrorMsg>
          <WizardActions>
            <BtnPrimary onClick={handleNextClick} sx={{ borderRadius: "8px" }} disabled={apiCall}>
                {apiCall?<CircularProgress size={"1.25rem"}/>:"Next"}
            </BtnPrimary>
          </WizardActions>
        </WizardCard>
    </Box>
    )
}

export default LeavePolicyDetails;